import log from 'loglevel';
import cloneDeep from 'lodash.clonedeep';

import { ConfigurationKeys, Configuration, ConfigurationChange } from '../configuration/configuration.model';
import { configDefinition } from '../configuration/config-definition';

/**
 * The ConfigurationManager is managing the configuration state of the SDK instance.
 */
export class ConfigurationManager {
  private static state: Configuration = ConfigurationManager.getDefaultConfig();

  /**
   * Returns the value for the provided configuration key.
   * @param key 
   */
  static get(key: ConfigurationKeys): any {
    return this.state[key];
  }

  /**
   * Returns a readonly copy of the current Configuration state.
   */
  static getConfigState(): Configuration {
    return Object.freeze(cloneDeep(this.state));
  }

  /**
   * Verifies the passed configuration for valid keys and values and purges invalid ones.
   * @param configuration 
   */
  static verifyConfiguration(configuration: {[key: string]: any}): void {
    for(let key in configuration) {
      if (!this.isConfigurationKey(key)) {
        log.warn(`The provided key '${key}' is not a valid configuration property.`);
        delete configuration[key];
      } else if(!this.isCorrectConfigurationType(key, configuration[key])) {
        log.warn(`The provided value for '${key}' is not a valid type. Valid types are: ${configDefinition[key].allowedTypes.join(', ')}.`);
        delete configuration[key];
      }
    }
  }

  /**
   * Applies the provided configuration to the current configuration state.
   * @param configuration 
   */
  static applyConfiguration(configuration: ConfigurationChange): void {
    if (Object.keys(configuration).length > 0) {
      log.debug('Apply new configuration to configuration state: ', configuration);
      Object.assign(this.state, configuration);
      for(let key in configuration) {
        const onSetFunction = configDefinition[<ConfigurationKeys>key].onSet;
        onSetFunction && onSetFunction(configuration[<ConfigurationKeys>key] as never); // why never??
      }
      window.ceDebugger?.pushConfiguration(this.state);
    };
  }

  private static getDefaultConfig(): Configuration {
    let state: any = {};
    for (let key in configDefinition) {
      state[key] = configDefinition[<ConfigurationKeys>key].default;
    }
    return state;
  }

  private static isConfigurationKey(key: string): key is ConfigurationKeys  {
    return Object.keys(this.state).includes(key);
  }

  private static isCorrectConfigurationType(key: ConfigurationKeys, value: any): boolean {
    let type: any;
    switch (value) {
      case (undefined):
        type = 'undefined';
        break;
      case (null):
        type = 'null';
        break;
      default:
        type = typeof value;
    }
    return configDefinition[key].allowedTypes.includes(type);
  }
}
